import { ComponentType, lazy, Suspense, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useSelector } from 'react-redux'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

import BillingPage from './Billing'
import EnterpriseSettingsPage from './Organization'
import PersonalSettings from './Personal'

import styles from './styles.module.scss'

import { Spinner } from '@/components/Spinner'
import Tabs from '@/components/Tabs'
import { useApiKeyManagementAccess } from '@/hooks/useApiKeyManagementAccess'
import { selectIsSalesOwned } from '@/store/features/user/userSlice'

const ApiKeysPage = lazy(() => import('./ApiKeys'))

// Single source of truth for the settings tab strip; App.tsx's routes derive their
// slugs from SETTINGS_TAB_SLUGS below so a new tab can't be added here without a route.
// Notifications moved into Personal ("Your Notifications") — it's a per-user preference,
// not an org-wide one, and only ever mattered to admins anyway.
const SETTINGS_TABS: Array<{ label: string; slug: string; Body: ComponentType }> = [
  { label: 'Organization', slug: 'organization', Body: EnterpriseSettingsPage },
  { label: 'Personal', slug: 'personal', Body: PersonalSettings },
  { label: 'Billing', slug: 'billing', Body: BillingPage },
  { label: 'API keys', slug: 'api-keys', Body: ApiKeysPage },
]

// An unknown suffix (e.g. /app/settings/other) intentionally falls through to index 0/Organization.
export const SETTINGS_TAB_SLUGS = SETTINGS_TABS.map((tab) => tab.slug)

const Settings = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const access = useApiKeyManagementAccess()
  const isSalesOwned = useSelector(selectIsSalesOwned)
  const visibleTabs = SETTINGS_TABS.filter(
    (tab) => (tab.slug !== 'api-keys' || access.isEnabled) && (tab.slug !== 'billing' || !isSalesOwned)
  )
  const isApiKeysRoute = location.pathname.endsWith('/api-keys')
  const isBillingRoute = location.pathname.endsWith('/billing')

  const getActiveTab = () => {
    const index = visibleTabs.findIndex((tab) => location.pathname.endsWith(`/${tab.slug}`))
    return index === -1 ? 0 : index // Default to organization
  }

  const handleTabChange = (index: number) => {
    navigate(`/app/settings/${visibleTabs[index].slug}`)
  }

  // Redirect to /organization if we're at /settings
  useEffect(() => {
    if (location.pathname === '/app/settings') {
      navigate('/app/settings/organization')
    }
  }, [location.pathname, navigate])

  if (isApiKeysRoute && access.isPending) return <div role="status">Loading API keys…</div>
  if (isApiKeysRoute && !access.isEnabled) return <Navigate to="/app/settings/organization" replace />
  if (isBillingRoute && isSalesOwned) return <Navigate to="/app/settings/organization" replace />

  const ActiveBody = visibleTabs[getActiveTab()].Body

  return (
    <>
      <Helmet>
        <title>Wispr Flow | Settings</title>
      </Helmet>
      <div className={`${styles.container} ${styles.settingsContainer}`}>
        <div className={`${styles.tabTitle} ${styles.pageTabTitle}`}>
          <h6>Settings</h6>
          <Tabs tabs={visibleTabs.map((tab) => tab.label)} activeTab={getActiveTab()} setActiveTab={handleTabChange} />
        </div>
        <div className={styles.tabbedContent}>
          <Suspense fallback={<Spinner size={24} />}>
            <ActiveBody />
          </Suspense>
        </div>
      </div>
    </>
  )
}

export default Settings
